import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { getPostBySlug, getRecentPosts } from '../data/blogPosts';
import { Clock, CalendarBlank, ArrowLeft, ArrowRight, ShareNetwork, WhatsappLogo } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Minimal markdown → HTML (supports h2, h3, **bold**, lists, tables, paragraphs)
function renderMarkdown(text) {
  const lines = text.split('\n');
  const out = [];
  let i = 0;
  let inList = false;
  let inTable = false;
  let tableRows = [];

  const flushList = () => {
    if (inList) { out.push('</ul>'); inList = false; }
  };
  const flushTable = () => {
    if (inTable && tableRows.length > 0) {
      const [header, , ...body] = tableRows;
      const headerCells = header.split('|').slice(1, -1).map(c => `<th>${c.trim()}</th>`).join('');
      const bodyRows = body.map(r => `<tr>${r.split('|').slice(1, -1).map(c => `<td>${c.trim()}</td>`).join('')}</tr>`).join('');
      out.push(`<div class="overflow-x-auto my-6"><table class="w-full border-collapse"><thead><tr class="border-b-2 border-primary/20">${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table></div>`);
      inTable = false;
      tableRows = [];
    }
  };

  const inline = (s) => s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');

  while (i < lines.length) {
    const line = lines[i];
    const trim = line.trim();

    if (trim.startsWith('|') && trim.endsWith('|')) {
      flushList();
      if (!inTable) inTable = true;
      tableRows.push(trim);
      i++;
      continue;
    } else {
      flushTable();
    }

    if (trim.startsWith('## ')) {
      flushList();
      out.push(`<h2>${inline(trim.slice(3))}</h2>`);
    } else if (trim.startsWith('### ')) {
      flushList();
      out.push(`<h3>${inline(trim.slice(4))}</h3>`);
    } else if (trim.startsWith('- ')) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${inline(trim.slice(2))}</li>`);
    } else if (trim === '') {
      flushList();
    } else {
      flushList();
      out.push(`<p>${inline(trim)}</p>`);
    }
    i++;
  }
  flushList();
  flushTable();
  return out.join('\n');
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [recent, setRecent] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const html = useMemo(() => post ? renderMarkdown(post.body) : '', [post]);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    // Try API first
    axios.get(`${API}/blog/${slug}`)
      .then(({ data }) => {
        // Normalize API response to legacy local shape
        setPost({
          slug: data.slug,
          title: data.title,
          metaDescription: data.meta_description,
          keywords: data.keywords,
          author: data.author,
          publishedAt: data.published_at,
          readMinutes: data.read_minutes,
          category: data.category,
          coverImage: data.cover_image,
          excerpt: data.excerpt,
          body: data.body,
          reviewedBy: data.reviewed_by,
          lastReviewed: data.last_reviewed,
        });
      })
      .catch(() => {
        const local = getPostBySlug(slug);
        if (local) setPost(local);
        else setNotFound(true);
      });

    // Recent posts list (try API, fall back to local)
    axios.get(`${API}/blog`)
      .then(({ data }) => {
        const others = (data.posts || []).filter(p => p.slug !== slug).slice(0, 3).map(p => ({
          slug: p.slug,
          title: p.title,
          category: p.category,
          coverImage: p.cover_image,
          readMinutes: p.read_minutes,
        }));
        setRecent(others.length ? others : getRecentPosts(3, slug));
      })
      .catch(() => setRecent(getRecentPosts(3, slug)));
  }, [slug]);

  if (notFound) return <Navigate to="/blog" replace />;
  if (!post) {
    return (
      <div className="min-h-screen bg-surface-secondary">
        <Navbar />
        <div className="pt-28 text-center text-secondary">Loading article...</div>
      </div>
    );
  }

  const shareUrl = encodeURIComponent(`https://seconddoctoropinion.com/blog/${post.slug}`);
  const shareText = encodeURIComponent(`${post.title} — `);

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="blog-post-page">
      <Navbar />
      <main className="pt-24 pb-16">
        {/* Hero with cover */}
        <div className="relative bg-primary text-white">
          <div className="absolute inset-0 opacity-30">
            <img src={post.coverImage} alt="" className="w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(45,74,43,0.4) 0%, rgba(45,74,43,0.95) 100%)' }} />
          </div>
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-6">
              <ArrowLeft size={16} weight="bold" /> Back to all articles
            </Link>
            <div className="flex items-center gap-3 text-xs mb-4">
              <span className="bg-accent text-white px-3 py-1 rounded-full font-semibold uppercase tracking-wider">{post.category}</span>
              <span className="flex items-center gap-1 text-white/85"><Clock size={14} /> {post.readMinutes} min read</span>
              <span className="flex items-center gap-1 text-white/85"><CalendarBlank size={14} /> {new Date(post.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
            <h1 className="font-spectral text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              {post.title}
            </h1>
            <p className="mt-4 text-white/85 text-base sm:text-lg leading-relaxed">{post.excerpt}</p>
            <p className="mt-4 text-sm text-white/80" data-testid="blog-post-byline">
              By {post.author}
              {post.reviewedBy && <span data-testid="blog-post-reviewed-by"> · Medically reviewed by {post.reviewedBy}</span>}
              {post.lastReviewed && <span data-testid="blog-post-last-reviewed"> · Last reviewed {new Date(post.lastReviewed).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
            </p>
          </div>
        </div>

        {/* Article body */}
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <div
            className="bg-surface rounded-3xl border border-primary/10 p-6 sm:p-10 prose-medical"
            data-testid="blog-post-body"
            dangerouslySetInnerHTML={{ __html: html }}
          />

          {/* Share + CTA */}
          <div className="mt-6 bg-surface rounded-2xl border border-primary/10 p-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-3">
              <ShareNetwork size={20} weight="bold" className="text-primary" />
              <span className="text-sm font-medium text-primary">Share this article:</span>
              <a
                href={`https://wa.me/?text=${shareText}${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-white px-3 py-1.5 rounded-full"
                style={{ backgroundColor: '#25D366' }}
                data-testid="share-whatsapp"
              >
                <WhatsappLogo size={14} weight="fill" /> WhatsApp
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm px-3 py-1.5 rounded-full border border-primary/30 text-primary hover:bg-primary/5"
              >
                Twitter
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm px-3 py-1.5 rounded-full border border-primary/30 text-primary hover:bg-primary/5"
              >
                Facebook
              </a>
            </div>
          </div>

          <div className="mt-6 bg-primary rounded-3xl p-8 text-center text-white">
            <h3 className="font-spectral text-xl sm:text-2xl font-bold mb-2">Get a Written Second Opinion in 48 Hours</h3>
            <p className="text-white/85 text-sm mb-5 max-w-xl mx-auto">
              Upload your reports, get expert review from an Indian specialist registered with the NMC or a State Medical Council. ₹1,500 Basic | ₹2,500 Premium with video.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-full font-semibold hover:bg-accent/90"
              data-testid="post-cta-register"
            >
              Start Your Consultation <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </article>

        {/* Recent posts */}
        {recent.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
            <h2 className="font-spectral text-2xl font-bold text-primary mb-6">Continue reading</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {recent.map((p, idx) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group bg-surface rounded-2xl overflow-hidden border border-primary/10 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                  data-testid={`related-post-${idx}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-secondary mb-2">{p.category} · {p.readMinutes} min</p>
                    <h3 className="font-spectral text-base font-semibold text-primary leading-snug group-hover:text-accent transition-colors line-clamp-3">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
